import { useParams } from 'react-router';
import { Box, Typography, Stack, Chip, Button } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { LoadingIndicator, Routes } from '@xolv-mybrightlink/common';
import { useNavigate } from 'react-router-dom';
import { useCourseDetail } from './hooks/useCourseDetail';
import { LessonDetail } from './lesson/LessonDetail';
import { useEffect, useMemo } from 'react';
import { ArrowBack, ArrowBackIos } from '@mui/icons-material';
import { MultilineText } from '@xolv-mybrightlink/common';
import _ from 'lodash';
import { useQuizzes } from './hooks/useQuizzes';
import { QuizPage } from './quiz/QuizPage';
import { markComplete } from './services/QuizService';
import { handlerError } from './utils/errorHandler';

export const CourseDetailPage = () => {
  const { courseId, lessonId, quizId } = useParams<{
    courseId: string;
    lessonId: string;
    quizId: string;
  }>();
  const intl = useIntl();
  const navigate = useNavigate();

  const {
    selectedCourseId,
    courseList,
    lessonList,
    completedLessonList,
    assignedLessonIds,
    loading,
  } = useCourseDetail(courseId);

  const { quizzes } = useQuizzes(courseId);

  useEffect(() => {
    async function onLmsMessage(event: { data: string }) {
      if (!lessonId) return;
      const index = _.findIndex(
        lessonList,
        (lesson) => lesson.id === +lessonId,
      );
      if (!courseId) return;
      const maxIndex = lessonList.length - 1;
      switch (event.data) {
        case 'lms_prev_lesson':
          navigate(
            Routes.resources.courses.lessonId(
              courseId,
              lessonList[Math.max((index - 1) % lessonList.length, 0)].id,
            ),
          );
          break;
        case 'lms_next_lesson':
          navigate(
            Routes.resources.courses.lessonId(
              courseId,
              lessonList[Math.min((index + 1) % lessonList.length, maxIndex)]
                .id,
            ),
          );
          break;
        case 'lms_quiz':
          if (!quizzes[+lessonId]) {
            try {
              // mark complete
              await markComplete(lessonId);
            } catch (e) {
              handlerError('Failed to mark lesson complete!');
            }
          } else {
            navigate(
              Routes.resources.courses.quizId(
                courseId,
                lessonId,
                quizzes[+lessonId],
              ),
            );
          }
          break;
      }
    }
    window.addEventListener('message', onLmsMessage);
    return () => window.removeEventListener('message', onLmsMessage);
  }, [courseId, lessonId, lessonList, navigate, quizzes]);

  const course = useMemo(() => {
    return courseList.find((course) => selectedCourseId === course.id);
  }, [selectedCourseId, courseList]);

  const lesson = useMemo(() => {
    return lessonId
      ? lessonList.find((lesson) => +lessonId === lesson.id)
      : null;
  }, [lessonId, lessonList]);

  return (
    <Box
      sx={{
        p: 10,
        height: '100%',
        overflow: 'hidden',
        backgroundImage:
          selectedCourseId === -1
            ? "url('/public/assets/images/course_not_found.svg')"
            : undefined,
        backgroundRepeat: 'no-repeat',
        backgroundSize: selectedCourseId === -1 ? '100% 100%' : undefined,
      }}
    >
      <Typography
        variant='h6'
        sx={{
          fontWeight: 500,
          color: selectedCourseId === -1 ? 'white' : '#3b4559',
        }}
      >
        <FormattedMessage id='sidebar.menu.parent-led-curriculum' />
      </Typography>
      {selectedCourseId !== -1 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            mt: 10,
            mb: 4,
            height: '100%',
          }}
        >
          <Box
            className='hide-scrollbar'
            sx={{
              width: '30%',
              mt: 2.5,
              pb: 10,
            }}
          >
            <Typography variant='h6' sx={{ color: '#3b4559', fontWeight: 600 }}>
              <FormattedMessage id='resources.education.courses' />
            </Typography>
            <Box
              className='hide-scrollbar'
              sx={{
                overflowY: 'auto',
                height: '90%',
              }}
            >
              {loading && courseList.length === 0 && (
                <LoadingIndicator sx={{ height: '100%' }} />
              )}
              {courseList.map((course) => (
                <Box
                  className='no-select'
                  key={course.id}
                  onClick={() =>
                    navigate(Routes.resources.courses.courseId(course.id))
                  }
                  sx={{
                    mt: 10,
                    cursor: 'pointer',
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      color:
                        selectedCourseId === course.id ? '#3b4559' : '#626a7a',
                      fontWeight:
                        selectedCourseId === course.id ? 600 : undefined,
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                      WebkitLineClamp: 2,
                    }}
                  >
                    {course.title}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        color:
                          selectedCourseId === course.id
                            ? '#3b4559'
                            : '#626a7a',
                        flex: 7,
                        mt: 1,
                      }}
                    >
                      {intl.formatMessage(
                        { id: 'resources.lessons.completed' },
                        {
                          completed: course.completed,
                          total: course.numOfLessons,
                        },
                      )}
                    </Typography>
                    {course?.completed === course?.numOfLessons ? (
                      <img
                        alt={course.title}
                        src='/public/assets/icons/ic_check_circle.svg'
                        style={{
                          width: 20,
                          height: 20,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          color: '#edf0f5',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: '0.5px solid #edf0f5',
                          bgcolor: '#edf0f5',
                          height: 10,
                          flex: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: course.percentage / 100,
                            height: 10,
                            background: '#00b2a9',
                            borderRadius: 3,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {!lesson ? (
            <Box
              sx={{
                background: '#fff',
                width: '100%',
                height: '90%',
                ml: 10,
                mt: 2.5,
                borderRadius: 3,
                boxSizing: 'border-box',
                boxShadow:
                  '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
                border: '1px solid #d8dade',
              }}
            >
              {loading && (
                <LoadingIndicator
                  sx={{
                    height: '100%',
                  }}
                />
              )}
              {!loading && (
                <Box sx={{ height: '100%' }}>
                  {/* Thumbnail */}
                  <Box
                    component='img'
                    src={course?.thumbnail}
                    sx={{
                      width: '100%',
                      height: '25%',
                      objectFit: 'cover',
                      borderRadius: '12px 12px 0px 0px',
                    }}
                  />
                  {/* Header */}
                  <Box sx={{ p: 10 }}>
                    <MultilineText
                      maxLines={2}
                      sx={{
                        color:
                          selectedCourseId === course?.id
                            ? '#3b4559'
                            : '#626a7a',
                        fontWeight:
                          selectedCourseId === course?.id ? 600 : undefined,
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis',
                        WebkitLineClamp: 2,
                      }}
                    >
                      {_.unescape(course?.title)}
                    </MultilineText>
                  </Box>
                  {/* Lesson list */}
                  <Box sx={{ px: 10, overflowY: 'auto', height: '50%' }}>
                    <Box>
                      {lessonList.map((lesson, index) => (
                        <Box
                          key={lesson.id}
                          onClick={() => {
                            if (!courseId) return;
                            navigate(
                              Routes.resources.courses.lessonId(
                                courseId,
                                lesson.id,
                              ),
                            );
                          }}
                          sx={{
                            mb: 10,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          <Stack
                            direction='row'
                            sx={{
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              sx={{
                                width: 'fit-content',
                                position: 'relative',
                                height: '100%',
                                mr: 5,
                                '&:after':
                                  index < lessonList.length - 1
                                    ? {
                                        content: '""',
                                        position: 'absolute',
                                        left: '50%',
                                        bottom: '-125%',
                                        width: '1px',
                                        height: '150%',
                                        borderLeft: '1px solid #b1b5bd',
                                      }
                                    : {},
                              }}
                            >
                              <Box
                                component='img'
                                src={
                                  completedLessonList.includes(lesson.id)
                                    ? '/public/assets/icons/ic_check_circle.svg'
                                    : '/public/assets/icons/ic_circle.svg'
                                }
                                sx={{
                                  height: 30,
                                  objectFit: 'cover',
                                }}
                              />
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <Typography
                                variant='body2'
                                sx={{
                                  '&:hover': {
                                    color: '#307fe2',
                                    fontWeight: 500,
                                  },
                                }}
                              >
                                {_.unescape(lesson.title)}
                              </Typography>
                              {_.includes(assignedLessonIds, lesson.id) && (
                                <Chip
                                  label={intl.formatMessage({
                                    id: 'resources.courses.assigned',
                                  })}
                                  sx={{
                                    bgcolor: '#00b2a9',
                                    color: '#fff',
                                    width: 86,
                                    height: 30,
                                    cursor: 'pointer',
                                  }}
                                />
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Stack
              sx={{
                background: '#fff',
                width: '100%',
                height: '90%',
                ml: 10,
                borderRadius: 3,
                boxSizing: 'border-box',
                boxShadow:
                  '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
                border: '1px solid #d8dade',
                p: 10,
              }}
            >
              {quizId ? (
                <QuizPage
                  onBack={() => {
                    if (!courseId || !lessonId) return;
                    navigate(
                      Routes.resources.courses.lessonId(courseId, +lessonId),
                    );
                  }}
                />
              ) : (
                <>
                  <Typography
                    variant='body2'
                    sx={{ color: '#307fe2', fontWeight: 500 }}
                  >
                    {_.unescape(course?.title)}
                  </Typography>
                  <Stack direction='row' sx={{ mt: 2.5, alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        if (!courseId) return;
                        navigate(Routes.resources.courses.courseId(+courseId));
                      }}
                    >
                      <ArrowBackIos sx={{ color: '#307fe2' }} />
                    </Box>
                    <Typography
                      variant='h6'
                      sx={{ color: '#3b4559', fontWeight: 600 }}
                    >
                      {_.unescape(lesson?.title)}
                    </Typography>
                  </Stack>
                  {lessonId && lessonList && (
                    <Box sx={{ mt: 2, height: 1 }}>
                      <LessonDetail
                        lessonId={lessonId}
                        hasQuiz={!!quizzes[+lessonId]}
                        firstLessonId={lessonList[0].id.toString()}
                        lastLessonId={lessonList[
                          lessonList.length - 1
                        ].id.toString()}
                      />
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            textAlign: 'center',
            color: 'white',
            pt: 40,
          }}
        >
          <Typography variant='h3' fontWeight={600}>
            <FormattedMessage id='resources.courses.error.oops' />
          </Typography>
          <Typography variant='h5' sx={{ pt: 5 }}>
            <FormattedMessage id='resources.courses.error.page-not-found' />
          </Typography>
          <Button
            variant='contained'
            disableElevation
            onClick={() => navigate(-1)}
            sx={{
              mt: 10,
              borderRadius: 30,
              py: 3.5,
              width: 200,
            }}
          >
            <ArrowBack sx={{ mr: 2.5 }} />
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <FormattedMessage id='common.go-back' />
            </Typography>
          </Button>
        </Box>
      )}
    </Box>
  );
};
